import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionLabel, EASE } from "./Reveal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Physiotherapy", "Strength & Conditioning", "Both"];

function Stars({ value, size = "h-4 w-4" }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= value ? "fill-[#8DA37F] text-[#8DA37F]" : "text-[#111111]/25"}`}
        />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", service: "Physiotherapy", rating: 5, text: "" });

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((res) => setReviews(res.data))
      .catch(() => toast.error("Could not load reviews right now."))
      .finally(() => setLoaded(true));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.text.trim().length < 10) {
      toast.error("Please add your name and a few words about your experience.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reviews`, form);
      setReviews((prev) => [res.data, ...prev]);
      setOpen(false);
      setForm({ name: "", service: "Physiotherapy", rating: 5, text: "" });
      toast.success("Thank you! Your review is now live.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-8 mb-14 md:mb-20">
          <div>
            <Reveal>
              <SectionLabel>Patient Reviews</SectionLabel>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-light text-[#111111] text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight mt-6">
                Stories from people
                <br />
                who <span className="italic text-[#8DA37F]">moved again.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="review-open-form-btn"
                  className="inline-flex items-center gap-3 text-xs tracking-[0.15em] uppercase font-medium px-8 py-4 rounded-full border border-[#111111]/25 text-[#111111] hover:border-[#111111] hover:-translate-y-0.5 transition-all duration-300"
                >
                  Share your story
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#F5F0E8] border-[#E5E0D8] sm:max-w-md" data-testid="review-form-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display text-3xl font-light text-[#111111]">
                    Share your story
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-5 mt-2">
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-medium text-[#111111]/60">
                      Your name
                    </label>
                    <Input
                      data-testid="review-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. Aarav Mehta"
                      className="mt-2 bg-white/60 border-[#E5E0D8] focus-visible:ring-[#8DA37F]"
                    />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-medium text-[#111111]/60">
                      Service
                    </label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {SERVICES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`review-service-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
                          onClick={() => setForm({ ...form, service: s })}
                          className={`text-xs tracking-wide px-4 py-2 rounded-full border transition-colors duration-300 ${
                            form.service === s
                              ? "bg-[#111111] text-[#F5F0E8] border-[#111111]"
                              : "border-[#111111]/25 text-[#111111]/70 hover:border-[#111111]"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-medium text-[#111111]/60">
                      Rating
                    </label>
                    <div className="mt-2 flex gap-2" data-testid="review-rating-input">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`review-star-${n}`}
                          onClick={() => setForm({ ...form, rating: n })}
                          className="transition-transform duration-200 hover:scale-125"
                        >
                          <Star
                            className={`h-6 w-6 ${n <= form.rating ? "fill-[#8DA37F] text-[#8DA37F]" : "text-[#111111]/25"}`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-medium text-[#111111]/60">
                      Your experience
                    </label>
                    <Textarea
                      data-testid="review-text-input"
                      value={form.text}
                      onChange={(e) => setForm({ ...form, text: e.target.value })}
                      placeholder="What changed for you?"
                      rows={4}
                      className="mt-2 bg-white/60 border-[#E5E0D8] focus-visible:ring-[#8DA37F]"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="review-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#111111] text-[#F5F0E8] text-xs tracking-[0.15em] uppercase font-medium px-7 py-4 rounded-full hover:bg-[#8DA37F] hover:text-[#111111] transition-colors duration-300 disabled:opacity-60"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {submitting ? "Sharing..." : "Publish review"}
                  </button>
                </form>
              </DialogContent>
            </Dialog>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
          <AnimatePresence>
            {reviews.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: EASE }}
                data-testid={`review-card-${i}`}
                className="border border-[#E5E0D8] bg-[#efe9dd]/50 p-8 flex flex-col hover:bg-[#efe9dd] transition-colors duration-500"
              >
                <Quote className="h-6 w-6 text-[#8DA37F] fill-[#8DA37F]/30" />
                <p className="mt-5 text-base leading-relaxed text-[#111111]/80 flex-1">
                  {r.text}
                </p>
                <div className="mt-7 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-display text-xl text-[#111111]">{r.name}</p>
                    <p className="text-[11px] tracking-[0.15em] uppercase text-[#6b7f5e] mt-1">
                      {r.service}
                    </p>
                  </div>
                  <Stars value={r.rating} />
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {loaded && reviews.length === 0 && (
          <Reveal>
            <div
              className="border border-dashed border-[#8DA37F]/50 py-20 px-8 text-center"
              data-testid="reviews-empty-state"
            >
              <p className="font-display text-3xl md:text-4xl font-light text-[#111111]">
                Stories are <span className="italic text-[#8DA37F]">on their way.</span>
              </p>
              <p className="mt-4 text-base text-[#111111]/60 max-w-md mx-auto">
                Patient reviews will appear here soon — be the first to share
                your experience with Physiora.
              </p>
            </div>
          </Reveal>
        )}

        {!loaded && (
          <p className="text-center text-[#111111]/50 py-16" data-testid="reviews-loading">
            Loading stories...
          </p>
        )}
      </div>
    </section>
  );
}
